import type { PhoneNumberWithPrefix } from "@/modules/box/boxTypes"
import { type AddressDto, convertAddressDraftToDto } from "@modules/address/addressDto"
import { type AddressDraft } from "@modules/address/addressTypes"
import { WARNING_OFFER_END_DELAY } from "@modules/boxSubscription/entities/boxSubscriptionConst"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { word } from "@modules/core/utils/i18n"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import {
  type BoxMemberNextBookingDto,
  type MemberActiveOfferDto,
  type MemberDetailedDraftDto,
  type MemberDetailedDto,
  type MemberDetailedUpadteInput,
  type MemberItemDto,
  MemberStatus,
} from "@modules/members/gateway/memberDto"
import { PublicProfileEntity } from "@modules/profile/publicProfile/entities/publicProfileEntity"
import { type HUImage } from "@modules/types/imageTypes"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import dayjs from "dayjs"

export type MemberDetailedState = Omit<
  MemberDetailedEntity,
  "getInputUpdate" | "getFormikDraft" | "setNote" | "convertNoShow"
>

export type MemberItemState = Omit<MemberItemEntity, "getUserName">

export class MemberItemEntity {
  id: string
  avatar?: HUImage
  firstname: string
  lastname: string
  username: string
  status: MemberStatus
  note?: string
  lastVisited?: Date
  noShowPastMonth?: number
  activeOffers: ActiveOfferEntity[]
  archivedDate?: Date
  inscriptionDate?: Date
  archived?: boolean
  lastMembership?: string

  private constructor(memberItem: MemberItemState) {
    this.id = memberItem.id
    this.avatar = memberItem.avatar
    this.firstname = memberItem.firstname
    this.lastname = memberItem.lastname
    this.username = memberItem.username
    this.status = memberItem.status
    this.lastVisited = memberItem.lastVisited
    this.noShowPastMonth = memberItem.noShowPastMonth
    this.note = memberItem.note
    this.activeOffers = memberItem.activeOffers
    this.archivedDate = memberItem.archivedDate
    this.inscriptionDate = memberItem.inscriptionDate
    this.archived = memberItem.archived
    this.lastMembership = memberItem.lastMembership
  }

  static fromItemDto(dto: MemberItemDto): MemberItemEntity {
    return new MemberItemEntity({
      ...dto,
      avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
      lastVisited: dto.lastVisited ? new Date(dto.lastVisited) : undefined,
      noShowPastMonth: dto.noShowPastMonth ? dto.noShowPastMonth : 0,
      note: dto.note ? dto.note : "",
      activeOffers: dto.activeOffers.map((offer) => ActiveOfferEntity.fromDto(offer)),
      inscriptionDate: dto.inscriptionDate ? new Date(dto.inscriptionDate) : undefined,
      archivedDate: dto.archivedDate ? new Date(dto.archivedDate) : undefined,
      archived: dto.archived ?? undefined,
      lastMembership: dto.lastMembership ?? undefined,
    })
  }

  getUserName(): string {
    if (this.username === "") {
      return this.username
    }
    return "@" + this.username
  }
}

export class MemberDetailedEntity {
  id: string
  userId: string
  firstname: string | null
  lastname: string | null
  username: string
  email: string
  status: MemberStatus
  note: string | null
  phoneWithPrefix: PhoneNumberWithPrefix | null
  inscriptionDate: Date
  lastVisited: Date | null
  birthdate: Date | null
  address: AddressDto | null
  emergencyInformation: string | null
  noShowPastMonth: number
  activeOffersTitles: string[] | null
  nextBookings: BoxMemberNextBooking[]
  publicProfile: PublicProfileEntity | null

  private constructor(memberDetailed: MemberDetailedState) {
    this.id = memberDetailed.id
    this.userId = memberDetailed.userId
    this.firstname = memberDetailed.firstname
    this.lastname = memberDetailed.lastname
    this.inscriptionDate = memberDetailed.inscriptionDate
    this.username = memberDetailed.username
    this.birthdate = memberDetailed.birthdate
    this.phoneWithPrefix = memberDetailed.phoneWithPrefix
    this.email = memberDetailed.email
    this.address = memberDetailed.address
    this.emergencyInformation = memberDetailed.emergencyInformation ?? ""
    this.note = memberDetailed.note
    this.status = memberDetailed.status
    this.lastVisited = memberDetailed.lastVisited
    this.noShowPastMonth = memberDetailed.noShowPastMonth
    this.activeOffersTitles = memberDetailed.activeOffersTitles
    this.nextBookings = memberDetailed.nextBookings
    this.publicProfile = memberDetailed.publicProfile
  }

  static fromDto(dto: MemberDetailedDto): MemberDetailedEntity {
    return new MemberDetailedEntity({
      ...dto,
      birthdate: dto.birthDate ? new Date(dto.birthDate) : null,
      phoneWithPrefix: dto.phoneWithPrefix
        ? {
            number: dto.phoneWithPrefix.number,
            countryCode: dto.phoneWithPrefix.countryCode,
          }
        : null,
      status: dto.status,
      inscriptionDate: new Date(dto.inscriptionDate),
      lastVisited: dto.lastVisited ? new Date(dto.lastVisited) : null,
      publicProfile: dto.publicProfile ? PublicProfileEntity.fromDto(dto.publicProfile) : null,
      nextBookings: dto.nextBookings
        ? dto.nextBookings.map((nextBooking) => BoxMemberNextBooking.fromDto(nextBooking))
        : [],
      noShowPastMonth: dto.noShowPastMonth || 0,
    })
  }

  convertNoShow = () => {
    if (!this.noShowPastMonth) return 0

    if (this.noShowPastMonth > 3) {
      return 1
    } else if (this.noShowPastMonth >= 1 && this.noShowPastMonth <= 2) {
      return 2
    } else {
      return 3
    }
  }

  setNote(note: string): MemberDetailedEntity {
    return new MemberDetailedEntity({
      ...this,
      note,
    })
  }

  getInputUpdate(): MemberDetailedUpadteInput {
    return {
      firstname: this.firstname,
      lastname: this.lastname,
      birthDate: this.birthdate ? dayjs(this.birthdate).format(defaultDateFormat) : undefined,
      phoneWithPrefix: this.phoneWithPrefix,
      address: this.address ?? undefined,
      emergencyInformation: this.emergencyInformation ?? "",
      note: this.note || "",
    }
  }
}

export class MemberDetailedDraftEntity {
  firstname: string
  lastname: string
  username?: string
  birthDate?: Date
  phoneNumber: string
  email: string
  address: AddressDraft
  emergencyInformation: string
  note: string
  activeOffersTitles?: string[]

  private constructor(memberDetailedDraft: MemberDetailedDraftEntity) {
    this.firstname = memberDetailedDraft.firstname
    this.lastname = memberDetailedDraft.lastname
    this.username = memberDetailedDraft.username
    this.birthDate = memberDetailedDraft.birthDate
    this.phoneNumber = memberDetailedDraft.phoneNumber
    this.email = memberDetailedDraft.email
    this.address = memberDetailedDraft.address
    this.emergencyInformation = memberDetailedDraft.emergencyInformation
    this.note = memberDetailedDraft.note
    this.activeOffersTitles = memberDetailedDraft.activeOffersTitles
  }

  static toDto(draft: MemberDetailedDraftEntity): MemberDetailedDraftDto {
    return {
      email: draft.email,
      firstname: draft.firstname,
      lastname: draft.lastname,
      username: draft.username?.trim() || undefined,
      birthDate: draft.birthDate ? dayjs(draft.birthDate).format(defaultDateFormat) : undefined,
      phone: draft.phoneNumber,
      address: convertAddressDraftToDto(draft.address),
      emergencyInformation: draft.emergencyInformation,
      note: draft.note,
    }
  }
}

export class BoxMemberNextBooking {
  slotId: string
  name: string
  date: Date
  color: string

  private constructor(boxMemberNextBooking: BoxMemberNextBooking) {
    this.slotId = boxMemberNextBooking.slotId
    this.name = boxMemberNextBooking.name
    this.date = boxMemberNextBooking.date
    this.color = boxMemberNextBooking.color
  }

  static fromDto(dto: BoxMemberNextBookingDto): BoxMemberNextBooking {
    return new BoxMemberNextBooking({
      ...dto,
      date: new Date(dto.date),
    })
  }
}

export type ActiveOfferState = Omit<ActiveOfferEntity, "getWarning">
export class ActiveOfferEntity {
  offerName: string
  endValidity: Date | null
  endSubscription: Date | null
  subscriptionStatus: BoxSubscriptionStatus | null

  private constructor(activeOffer: ActiveOfferState) {
    this.offerName = activeOffer.offerName
    this.endValidity = activeOffer.endValidity
    this.endSubscription = activeOffer.endSubscription
    this.subscriptionStatus = activeOffer.subscriptionStatus
  }

  static fromDto(dto: MemberActiveOfferDto): ActiveOfferEntity {
    return new ActiveOfferEntity({
      ...dto,
      endValidity: dto.endValidity ? new Date(dto.endValidity) : null,
      endSubscription: dto.endSubscription ? new Date(dto.endSubscription) : null,
      subscriptionStatus: dto.subscriptionStatus ?? null,
    })
  }

  getWarning(): { warningContent: string } | undefined {
    if (
      this.subscriptionStatus === BoxSubscriptionStatus.PENDING_CANCELLATION &&
      this.endSubscription &&
      dayjs(this.endSubscription).diff(dayjs(), "day") <= WARNING_OFFER_END_DELAY
    )
      return {
        warningContent: word("box.member.table.offer.item.status.ends", {
          count: dayjs(this.endSubscription).startOf("day").diff(dayjs().startOf("day"), "day"),
        }),
      }
    else if (this.subscriptionStatus === BoxSubscriptionStatus.PAYMENT_IN_ERROR)
      return {
        warningContent: word("box.members.tab.offer.status.paymentError"),
      }
  }
}
